import { Button } from '@dynamika-shared/ui/Button/Button';
import { twMerge } from 'tailwind-merge';
import { CloseIcon, SearchIcon } from '@dynamika-shared/ui/icons';
import { Input } from 'antd';
import { Switch } from '@dynamika-shared/ui/Switch/Switch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { QC_TASK_STATUSES } from '@restore/QCTasksList/QCTasksList.types';
import { usePaginationAndSort } from '@restore/helpers';
import { useParams } from 'react-router-dom';
import { useQCTasksListSlice } from '@restore/QCTasksList/QCTasksList.slice';
import { useAuthSlice } from '@restore/auth/auth.slice';

const BUTTONS_FILTERS = [
  { title: 'All', id: '' },
  { title: 'Pending', id: QC_TASK_STATUSES.PENDING },
  { title: 'Done', id: QC_TASK_STATUSES.DONE },
  { title: 'In Progress', id: QC_TASK_STATUSES.IN_PROGRESS },
  { title: 'Query Open', id: QC_TASK_STATUSES.QUERY_OPEN },
  { title: 'On Hold', id: QC_TASK_STATUSES.ON_HOLD },
  { title: 'Failed', id: QC_TASK_STATUSES.FAILED },
];

export const QcListFilters = () => {
  const userData = useAuthSlice(s => s.data);
  const [filters, setFilters] = useQCTasksListSlice((state, actions) => [
    state.filters,
    actions.setFilters,
  ]);
  const [searchInputValue, setSearchInputValue] = useState(filters.patientCode);

  const { setCurrentPage } = usePaginationAndSort('QCList');

  const { id: clinicalTrialId } = useParams<{
    id: string;
    taskId: string;
  }>();

  useEffect(() => {
    setSearchInputValue('');
  }, [clinicalTrialId]);

  const resetPage = useCallback(() => {
    setCurrentPage({ listName: 'QCList', newPage: 1 });
    setFilters({ filterQuery: 'page', filterValue: 1 });
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(nextValue => {
        setFilters({ filterQuery: 'patientCode', filterValue: nextValue });
        resetPage();
      }, 500),
    [resetPage]
  );

  return (
    <div className='flex flex-wrap justify-between'>
      <div className='flex max-w-[745px] flex-wrap gap-1 pb-[24px]'>
        {BUTTONS_FILTERS.map(({ title, id }) => (
          <Button
            key={title}
            onClick={() => {
              if (id !== filters.status) {
                setFilters({ filterQuery: 'status', filterValue: id });
                resetPage();
              }
            }}
            className={twMerge(
              'min-w-[7rem] bg-white text-black hover:bg-waterLight py-[10px] px-[20px] ',
              filters.status === id && 'bg-waterLight'
            )}
            type='default'
            skipBasicStyles
          >
            {title}
          </Button>
        ))}
      </div>
      <div className='flex items-center gap-3 pb-[24px]'>
        <Switch
          size='small'
          label=''
          checked={!!filters.assignee}
          onChange={checked => {
            const assignedUser = checked
              ? userData?.['dynamika7/user_metadata']?.uuid
              : '';
            setFilters({ filterQuery: 'assignee', filterValue: assignedUser });
            resetPage();
          }}
          name='filter_assigned'
          extraLabel='Assigned to me'
          classNameInputWrapper='flex justify-between items-center h-[3.2rem] !gap-2'
        />

        <Input
          placeholder='Search Subject ID...'
          className='min-w-[400px] max-w-lg rounded-[32px] px-2 py-[10px] text-1424 text-spanishGray'
          suffix={
            filters.patientCode?.length ? (
              <CloseIcon
                className='h-2.5 w-2.5 cursor-pointer font-medium'
                onClick={() => {
                  setSearchInputValue('');
                  debouncedSearch('');
                }}
              />
            ) : (
              <SearchIcon className='h-2 w-2 cursor-pointer' />
            )
          }
          value={searchInputValue}
          onChange={({ target: { value } }) => {
            setSearchInputValue(value);
            debouncedSearch(value);
          }}
        />
      </div>
    </div>
  );
};
